import { useState, useEffect } from "react";
import { Card, Form, Button, Spinner, Alert } from "react-bootstrap";
import APIFunction from "../util/api";
import { INFO_STATEMENTS, SYSTEM_DEFAULTS } from "../util/constants";

export default function TransactionModal({ show, onClose }) {
  const [form, setForm] = useState({
    name: "",
    amount: "",
    category: "",
    phoneNumber: "",
    savingPlanId: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [savingsPlans, setSavingsPlans] = useState([]);
  const [loadingSavingsPlans, setLoadingSavingsPlans] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  // Fetch savings plans when modal is shown
  useEffect(() => {
    if (show) {
      fetchSavingsPlans();
      clearFields();
    }
  }, [show]);

  const fetchSavingsPlans = async () => {
    setLoadingSavingsPlans(true);
    try {
      const response = await APIFunction.getUserSavingsPlan(1); // Start with page 1
      if (response && response.data) {
        setSavingsPlans(response.data);
      }
    } catch (error) {
      console.error("Error fetching savings plans:", error);
      setSavingsPlans([]);
    } finally {
      setLoadingSavingsPlans(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Validate phone number format
    if (name === "phoneNumber") {
      // Only allow digits
      const digitsOnly = value.replace(/\D/g, "");
      
      // Validate format: must start with 254 and be 12 digits total
      if (digitsOnly.length > 0) {
        if (!digitsOnly.startsWith("254")) {
          setPhoneError("Phone number must start with 254");
        } else if (digitsOnly.length > 12) {
          // Prevent entering more than 12 digits
          return;
        } else if (digitsOnly.length < 12) {
          setPhoneError("Phone number must be 12 digits (254XXXXXXXXX)");
        } else {
          setPhoneError("");
        }
      } else {
        setPhoneError("");
      }
      
      setForm((prev) => ({ ...prev, [name]: digitsOnly }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const clearFields = () => {
    setForm({
      phoneNumber: "",
      amount: "",
      description: "",
      savingPlanId: "",
      category: "",
      name: "",
    });
    setStatus(null);
    setPhoneError("");
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Final validation before submission
    if (phoneError !== "" || form.phoneNumber.length !== 12 || !form.phoneNumber.startsWith("254")) {
      setPhoneError("Please enter a valid phone number starting with 254 (12 digits)");
      return;
    }
    
    setLoading(true);
    setStatus("waiting");
  
    // cleanup flag for unmount or close
    let cancelled = false;
  
    try {
      const payload = {
        phone: form.phoneNumber,
        amount: form.amount,
        description: form.description,
        savingPlanId: form.savingPlanId,
        category: form.category,
        name: form.name,
      };
  
      const stkResult = await APIFunction.mpesaSTKPush(payload);
  
      if (
        stkResult?.checkoutRequestId &&
        stkResult?.checkoutRequestId?.length > 0
      ) {
        const checkoutRequestId = stkResult.checkoutRequestId;
  
        let attempts = 0;
        const maxAttempts = 30; // ~2 minutes
  
        while (!cancelled && attempts < maxAttempts) {
          const statusRes = await APIFunction.pollMPesaPaymanetStatus(
            checkoutRequestId
          );
  
          if (cancelled) break; // stop immediately if cancelled
  
          if (statusRes.status === INFO_STATEMENTS.SUCCESS) {
            setStatus(INFO_STATEMENTS.SUCCESS);
            setLoading(false);
  
            setTimeout(() => {
              cancelled = true; // stop polling
              onClose();
            }, 2000);
  
            return;
          } else if (statusRes.status === INFO_STATEMENTS.FAILED) {
            setStatus(INFO_STATEMENTS.FAILED);
            setLoading(false);
  
            return;
          }
          await new Promise((resolve) => setTimeout(resolve, 3000));
          attempts++;
        }
  
        if (!cancelled) {
          setStatus("timeout");
          setLoading(false);
        }
      } else {
        setStatus(INFO_STATEMENTS.FAILED);
        setLoading(false);
      }
    } catch (err) {
      console.error(err);
      setStatus(INFO_STATEMENTS.FAILED);
      setLoading(false);
    }
  
      // Cleanup when component unmounts or modal closes
    return () => {
      cancelled = true;
    };
  };
  

  if (!show) return null;

  return (
    <div className="modal-backdrop">
      <Card className="transaction-modal-card">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="bi bi-credit-card me-2"></i>
            Make Payment
          </h5>
          <Button 
            variant="outline-secondary" 
            size="sm" 
            onClick={onClose}
            disabled={loading}
          >
            <i className="bi bi-x-lg"></i>
          </Button>
        </Card.Header>
        
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>
                <i className="bi bi-input-cursor-text me-2"></i> 
                Name
              </Form.Label>
              <Form.Control
                type="text"
                name="name"
                placeholder="Enter transaction name"
                value={form.name}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <i className="bi bi-currency-dollar me-2"></i>
                Amount
              </Form.Label>
              <Form.Control
                type="number"
                name="amount"
                placeholder="Enter amount"
                value={form.amount}
                onChange={handleChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <i className="bi bi-tag me-2"></i>
                Category
              </Form.Label>
              <Form.Select
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                >
                  <option value="">Select category</option>
                    {SYSTEM_DEFAULTS.TRANSACTIONS.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                  ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <i className="bi bi-phone me-2"></i>
                Phone Number
              </Form.Label>
              <Form.Control
                type="text"
                name="phoneNumber"
                placeholder="254XXXXXXXXX"
                value={form.phoneNumber}
                onChange={handleChange}
                isInvalid={phoneError !== ""}
                pattern="254[0-9]{9}"
                maxLength="12"
                required
              />
              <Form.Control.Feedback type="invalid">
                {phoneError || "Please enter a valid phone number starting with 254"}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                Enter phone number in format: 254XXXXXXXXX (12 digits)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>
                <i className="bi bi-piggy-bank me-2"></i>
                Savings Plan
              </Form.Label>
              {loadingSavingsPlans ? (
                <div className="d-flex align-items-center">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <span>Loading savings plans...</span>
                </div>
              ) : (
                <Form.Select
                  name="savingPlanId"
                  value={form.savingPlanId}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a savings plan</option>
                  {savingsPlans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} - {plan.description || 'No description'}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>
                <i className="bi bi-justify me-2"></i>
                description
              </Form.Label>
              <Form.Control
                type="text"
                name="description"
                placeholder="enter description (optional)"
                value={form.description}
                onChange={handleChange}
              />
            </Form.Group>

            {status === "waiting" && (
              <Alert variant="info" className="d-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-2" />
                Waiting for M-Pesa confirmation… please check your phone 📱
              </Alert>
            )}
            
            {status === INFO_STATEMENTS.SUCCESS && (
              <Alert variant="success">
                <i className="bi bi-check-circle me-2"></i>
                Payment successful ✅
              </Alert>
            )}
            
            {status === INFO_STATEMENTS.FAILED && (
              <Alert variant="danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Payment failed ❌ Please try again
              </Alert>
            )}

            <div className="d-flex gap-2">
              <Button 
                type="submit" 
                variant="primary" 
                disabled={loading}
                className="flex-fill"
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="bi bi-credit-card me-2"></i>
                    Pay Now
                  </>
                )}
              </Button>
              
              <Button 
                variant="outline-secondary" 
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
}